import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { ChartHandle } from './chart-handle';
import { chartLibraryPath, readChartRouteState } from './chart-routes';
import { ChartLibrary } from './ChartLibrary';
import { SingleChartView } from './SingleChartView';

// Router container for the charts feature. The `*` wildcard segment holds the chart handle when
// viewing a single chart — a saved `chart:<uuid>` or a brand-new, unsaved `draft:<uuid>`; absent, we
// show the library. Mirrors DashboardPage's container pattern — each child owns its own <Page> (the
// single view titles from the loaded chart's own name, matching DashboardView).
//
// A chart opened FROM the library renders as a modal over it rather than replacing it: the URL still
// moves to the chart's own address (so it can be copied, reloaded, and shared like any other), but the
// library stays mounted underneath — blurred, scroll position intact — and the modal's close button
// returns to it. Carried as navigation state (see ChartLibrary's navigate call), so a direct or shared
// link to the same URL still opens the chart as a full page.
export const ChartsPage = () => {
  const { '*': handle } = useParams<{ '*'?: string }>();
  const { cid } = useCompanyInfo();
  const navigate = useNavigate();
  const location = useLocation();
  const { fromChartLibrary = false } = readChartRouteState(location.state);
  const isModal = Boolean(handle) && fromChartLibrary;

  // Close by going BACK rather than pushing the library again: the entry before a modal is always the
  // library it was opened from (the view replaces, never pushes, its own URL changes — see
  // SingleChartView), so the browser's Back and Forward buttons stay where a user expects them. `key`
  // is 'default' only on a tab's first entry, where there is nothing in-app to go back to.
  const handleClose = () => {
    if (location.key === 'default') {
      navigate(chartLibraryPath(cid));
    } else {
      navigate(-1);
    }
  };

  return (
    <>
      {/* `inert` while the modal is up, so keyboard and screen-reader users can't reach the blurred
          library behind it. */}
      {(!handle || isModal) && (
        <div inert={isModal}>
          <ChartLibrary company={cid} />
        </div>
      )}
      {/* One render site for both the full page and the modal, so the view (and its picker state)
          survives a switch between the two. key={handle} forces a fresh mount per chart/draft: the
          charts route reuses this same component instance across handles (only the splat changes), so
          without a key its state — the `saving` flag and the transient date/KPI/breakdown picker
          overrides — would leak from one chart to the next (e.g. a stuck save spinner after a
          save-and-navigate). */}
      {handle && (
        <SingleChartView
          key={handle}
          company={cid}
          handle={handle as ChartHandle}
          onClose={isModal ? handleClose : undefined}
        />
      )}
    </>
  );
};
